"use client"
import { useState, useEffect, useRef } from "react"
import { Box, Typography, IconButton, List, styled, Drawer } from "@mui/material"
import { KeyboardArrowLeft as CollapseIcon, KeyboardArrowRight as ExpandIcon } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import IconWrapper from "./Icons/IconWrapper"
import { UsersIcon } from "./Icons/UserIcon"
import { MigrationIcon } from "./Icons/MigrationIcon"
import { AIViewIcon } from "../Icons/ViewComments/ViewComments"

// Componente estilizado para los elementos del menú
const MenuItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== "active" && prop !== "collapsed" && prop !== "disabled",
})<{ active: boolean; collapsed: boolean; disabled?: boolean }>(({ theme, active, collapsed, disabled = false }) => ({
    display: "flex",
    alignItems: "center",
    padding: collapsed ? "12px" : "12px 16px",
    cursor: disabled ? "not-allowed" : "pointer",
    borderRadius: "8px",
    position: "relative",
    marginBottom: "8px",
    backgroundColor: active ? "#ffffff" : "transparent",
    boxShadow: active ? "0px 2px 4px rgba(0, 0, 0, 0.05)" : "none",
    justifyContent: collapsed ? "center" : "flex-start",
    opacity: disabled ? 0.5 : 1,
    "&:hover": {
        backgroundColor: disabled ? "transparent" : active ? "#ffffff" : "rgba(0, 0, 0, 0.04)",
    },
    "&::before":
        active && !disabled
            ? {
                content: '""',
                position: "absolute",
                left: 0,
                top: "12px",
                bottom: "10px",
                width: "4px",
                borderRadius: "0 2px 2px 0",
                background: "linear-gradient(to bottom, #b41b3a, #f5a623)",
            }
            : {},
}))

interface AdminSidebarProps {
    open: boolean
    onToggle: () => void
    onCollapsedChange?: (collapsed: boolean) => void
}

export default function AdminSidebar({ open, onToggle, onCollapsedChange }: AdminSidebarProps) {
    const navigate = useNavigate()
    const [collapsed, setCollapsed] = useState(false)
    const [showTitle, setShowTitle] = useState(!collapsed)
    const [activeMenuItem, setActiveMenuItem] = useState<string>("migrations") // Cambiado a "migrations" por defecto
    const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const handleToggleCollapse = () => {
        const newCollapsed = !collapsed
        setCollapsed(newCollapsed)

        // Limpiamos cualquier timeout pendiente
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current)
        }

        // Si estamos colapsando, ocultamos el título inmediatamente
        if (newCollapsed) {
            setShowTitle(false)
        } else {
            // Si estamos expandiendo, esperamos a que termine COMPLETAMENTE la transición
            transitionTimeoutRef.current = setTimeout(() => {
                setShowTitle(true)
            }, 150) // Reducido de 300ms a 150ms para que aparezca más rápido
        }

        if (onCollapsedChange) {
            onCollapsedChange(newCollapsed)
        }
    }

    // Limpiamos el timeout cuando el componente se desmonta
    useEffect(() => {
        return () => {
            if (transitionTimeoutRef.current) {
                clearTimeout(transitionTimeoutRef.current)
            }
        }
    }, [])

    // Inicializamos el estado de showTitle
    useEffect(() => {
        setShowTitle(!collapsed)
    }, [collapsed])

    // Definimos los elementos del menú con la propiedad disabled
    const menuItems = [
        {
            id: "users",
            text: "User Management",
            icon: <UsersIcon />,
            active: activeMenuItem === "users",
            onClick: () => {
                if (!menuItems[0].disabled) {
                    setActiveMenuItem("users")
                    navigate("/users")
                }
            },
            disabled: true, // Deshabilitado
        },
        {
            id: "migrations",
            text: "Migration Management",
            icon: <MigrationIcon />,
            active: activeMenuItem === "migrations",
            onClick: () => {
                setActiveMenuItem("migrations")
                navigate("/migrations")
            },
            disabled: false, // Habilitado
        },
        {
            id: "prompts",
            text: "Prompt Management",
            icon: <AIViewIcon />,
            active: activeMenuItem === "prompts",
            onClick: () => {
                if (!menuItems[2].disabled) {
                    setActiveMenuItem("prompts")
                    navigate("/prompts")
                }
            },
            disabled: true, // Deshabilitado
        },
    ]

    const expandedWidth = 280
    const collapsedWidth = 80

    return (
        <Drawer
            variant="permanent"
            sx={{
                // Mantener la estructura del Drawer pero modificar sus estilos para que coincida con la imagen

                // Cambiar el estilo del Drawer para que tenga el fondo correcto
                "& .MuiDrawer-paper": {
                    position: "relative",
                    width: collapsed ? collapsedWidth : expandedWidth,
                    boxSizing: "border-box",
                    backgroundColor: "#f9f9f9",
                    border: "none",
                    boxShadow: "none",
                    overflowX: "hidden",
                    padding: "16px",
                    transition: "width 0.3s ease",
                    height: "100%",
                    top: "auto",
                    borderRight: "1px solid rgba(0, 0, 0, 0.08)",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: collapsed ? "center" : "space-between",
                        mb: 4,
                        mt: 1,
                        height: "32px", // Altura fija para evitar saltos
                    }}
                >
                    <Box
                        sx={{
                            opacity: showTitle ? 1 : 0,
                            transition: "opacity 0.15s ease", // Reducido de 0.2s a 0.15s
                            position: "absolute",
                            left: "16px",
                            pointerEvents: showTitle ? "auto" : "none",
                        }}
                    >
                        {!collapsed && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: "#424242",
                                    fontSize: "18px",
                                    opacity: 0.8,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    letterSpacing: "0.02em",
                                }}
                            >
                                API SMART MIGRATOR
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        onClick={handleToggleCollapse}
                        sx={{
                            color: "#424242",
                            ml: collapsed ? 0 : "auto",
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        {collapsed ? <ExpandIcon /> : <CollapseIcon />}
                    </IconButton>
                </Box>

                <List sx={{ px: 0, flexGrow: 1, overflowY: "auto" }}>
                    {menuItems.map((item) => (
                        <MenuItem
                            key={item.id}
                            active={item.active}
                            collapsed={collapsed}
                            disabled={item.disabled}
                            onClick={item.onClick}
                        >
                            <IconWrapper active={item.active} collapsed={collapsed}>
                                {item.icon}
                            </IconWrapper>
                            {!collapsed && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: "1rem",
                                            color: item.disabled ? "#9e9e9e" : "#424242",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            opacity: showTitle ? 1 : 0,
                                            transition: "opacity 0.15s ease",
                                        }}
                                    >
                                        {item.text}
                                    </Typography>
                                </Box>
                            )}
                        </MenuItem>
                    ))}
                </List>
            </Box>
        </Drawer>
    )
}

